import type * as Sentry from '@sentry/react';

export const sentryIgnoreErrors = [
  'Failed to load Stripe.js',
  /is not a valid JavaScript MIME type/,
  /^expected expression, got/,
  /^cancelled$/,
  'NetworkError when attempting to fetch resource.',
  'Failed to fetch',
  '[NotFoundError]',
  'Request failed with status code 403',
  'Object Not Found Matching Id',
  /ResizeObserver/,
  'AbortError: The user aborted a request',
  'AbortError: signal is aborted without reason',
  /Importing a module script failed./,
  'Load failed',
  /dynamically imported module/,
  /Importing binding name .+ is not found/,
  /Cannot get CSS styles from text's parentNode/,
  /For more details, see the full error text at/, // TODO - add id’s to graphql queries when applicable
  /Invalid call to runtime\.sendMessage\(\)/,
  /Stylesheet append timeout/,
  /\(type=unhandledrejection\) captured as promise rejection/,
  /captured as promise rejection with keys: \[object has no keys\]/,
];

export const IMPERSONATION_MUTATION_BLOCKED = 'IMPERSONATION_MUTATION_BLOCKED';

const RECOVERED_AUTH_ERROR_CODES = ['UNAUTHENTICATED', 'ACCOUNT_DELETED', 'USER_NOT_FOUND'];

export const SENTRY_IGNORE_ERROR_CODES = [
  IMPERSONATION_MUTATION_BLOCKED,
  ...RECOVERED_AUTH_ERROR_CODES,
];

type ErrorWithGraphQLBody = {
  graphQLErrors?: { extensions?: { code?: string } }[];
  networkError?: { result?: { errors?: { extensions?: { code?: string } }[] } };
  result?: { errors?: { extensions?: { code?: string } }[] };
};

export function shouldIgnoreSentryEvent(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  const exception = hint?.originalException as unknown as ErrorWithGraphQLBody | undefined;
  const extra = event.extra?.networkError as unknown as ErrorWithGraphQLBody | undefined;
  return [
    ...(exception?.graphQLErrors ?? []),
    ...(exception?.networkError?.result?.errors ?? []),
    ...(extra?.result?.errors ?? []),
  ].some((err) => SENTRY_IGNORE_ERROR_CODES.includes(err?.extensions?.code ?? ''));
}

type ErrorWithGraphQLErrors = {
  graphQLErrors?: {
    extensions?: { code?: string; fieldName?: string };
    path?: ReadonlyArray<string | number>;
  }[];
};

export function fingerprintApolloErrorEvent(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): Sentry.ErrorEvent {
  const exception = hint?.originalException as unknown as ErrorWithGraphQLErrors | undefined;
  const [firstGraphQLError] = exception?.graphQLErrors ?? [];
  if (!firstGraphQLError) return event;

  const { extensions, path } = firstGraphQLError;
  return {
    ...event,
    fingerprint: [
      'apollo-error-boundary',
      extensions?.code ?? 'no-code',
      extensions?.fieldName ?? String(path?.[0] ?? 'no-field'),
    ],
  };
}
